import React, { useState, useRef, useEffect, useCallback } from 'react';
import { TagBox } from 'devextreme-react/tag-box';
import { DateBox } from 'devextreme-react/date-box';
import { SelectBox } from 'devextreme-react/select-box';
import 'devextreme-react/text-area';
import { groupBy } from '../../utils/miscUtil';
import Form, {
  SimpleItem, Item, GroupItem, TabbedItem, TabPanelOptions, Tab,
  Label, RequiredRule, RangeRule, StringLengthRule, ButtonItem, EmptyItem,
} from 'devextreme-react/form';

import {sifTimesOfEvent} from '../../api/api'


var basins = require('../../api/Basins.json');
basins = basins.features.map((el) => ({value:el.properties.BASIN_NAME}))
basins.sort((a,b) => (a.value > b.value) ? 1 : ((b.value > a.value) ? -1 : 0))

var states = require('../../api/States.json');
states.sort((a,b) => (a.State > b.State) ? 1 : ((b.State > a.State) ? -1 : 0))

var counties = require('../../api/Counties.json');
counties = groupBy(counties, 'State')

const GroupCaption = ({ iconName, caption }) => (
  <React.Fragment>
    <i className={`${iconName} me-2`}></i>
    <span>{caption}</span>
  </React.Fragment>
);

const renderCalendarCaption = (data) => (
  <GroupCaption iconName="far fa-calendar-alt" {...data} />
);
const renderLocationCaption = (data) => (
  <GroupCaption iconName="fas fa-map-marked-alt" {...data} />
);
const renderDescriptionCaption = (data) => (
  <GroupCaption iconName="far fa-file-alt" {...data} />
);

export default function SIFEventDetails(props) {
  const [eventCountyOptions, setEventCountyOptions] = useState({});
  const [sifEvent, setSifEvent] = useState({});
  const [valid, setValid] = useState(false);
  const now = new Date();
  const minDate = new Date().setDate(now.getDate()-2000);
  const maxDate = new Date().setDate(now.getDate());
  const formRef = useRef();

  useEffect(() => {
    if (!props.data) {
      return;
    }
    let data = { ...props.data };
    if (!data['Type']) {
      if (data['Potential or Actual SIF']) {
        data['Type'] = data['Potential or Actual SIF'];
      } else if ((parseInt(data['Number of Fatalities']) || 0) + (parseInt(data['Number of Serious Injuries']) || 0) > 0) {
        data['Type'] = "Actual";
      } else {
        data['Type'] = "Potential";
      }
    }
    setSifEvent(data);
    if (data['State']) {
      for (var i = 0; i < states.length; i++) {
        if (data['State'] === states[i].Abbr) {
          setEventCountyOptions({
            dataSource: counties[states[i].State],
            activeStateEnabled: true,
            valueExpr: 'County',
            displayExpr: 'County',
            searchEnabled: true,
          });
        }
      }
    }
  }, [props.data]);

  const onStateSelected = useCallback((e) => {
    for (var i=0; i<states.length; i++) {
      if (e.value===states[i].Abbr) {
        setEventCountyOptions({
          dataSource:counties[states[i].State],
          activeStateEnabled: true,
          valueExpr: 'County',
          displayExpr: 'County',
          searchEnabled: true,
        });
      }
    }
  }, [setEventCountyOptions]);

  const onTypeSelected = (e) => {
    let fd = formRef.current?.instance().option("formData");
    if (fd) {
      if (e.value !== "Actual") {
        fd['Number of Fatalities'] = 0;
        fd['Number of Serious Injuries'] = 0;
      }
      fd['Type'] = e.value;
      setSifEvent({ ...fd });
      props.onDataChanged({ ...fd });
    }
  };

  const onNumberChanged = (e) => {
    let fd = formRef.current?.instance().option("formData");
    if (fd) {
      setSifEvent({ ...fd });
      props.onDataChanged({ ...fd });
    }
  };

  const eventNumberEditorOptions={min: 0, max: 100, step: 0, onValueChanged: onNumberChanged};

  const onCTA = useCallback(() => {
    let formInstance = formRef.current?.instance();
    if (!formInstance) return;
    let v_data = formInstance.validate();
    let currentFormData = formInstance.option("formData") || sifEvent;
    const fatalities = parseInt(currentFormData["Number of Fatalities"]) || 0;
    const injuries = parseInt(currentFormData["Number of Serious Injuries"]) || 0;
    const eventType = currentFormData["Type"];
    const isActualValid = eventType === "Actual" && (fatalities + injuries) > 0;
    const isPotentialValid = eventType === "Potential";

    const isValid = v_data.isValid && (isActualValid || isPotentialValid);
    props.onValidData(isValid);
    if (isValid) {
      props.onCTA();
    }
  }, [sifEvent, props]);

  const onFieldDataChanged = useCallback((e) => {
    const formData = e.component.option("formData");
    setSifEvent({...formData});
    props.onDataChanged(formData);
    let v_data = e.component.validate();
    props.onValidData(v_data.isValid);
  }, [props]);

  const eventDateEditorOptions = {
    min: minDate,
    max: maxDate,
    width: '100%',
    showDropDownButton: false,
    onFocusIn:(e) => {e.component.open();}
  };
  const eventTimeEditorOptions = {
    dataSource:sifTimesOfEvent,
    activeStateEnabled: true,
    valueExpr: 'value',
    displayExpr: 'value',
    searchEnabled: true,
  };

  const eventTypeOptions = {
    dataSource:[{value:"Actual"}, {value:"Potential"}],
    activeStateEnabled: true,
    valueExpr: 'value',
    displayExpr: 'value',
    searchEnabled: false,
    onValueChanged: onTypeSelected,
  };

  const eventBasinOptions = {
    dataSource:basins,
    activeStateEnabled: true,
    valueExpr: 'value',
    displayExpr: 'value',
    searchEnabled: true,
  };

  const eventStateOptions = {
    dataSource:states,
    activeStateEnabled: true,
    valueExpr: 'Abbr',
    displayExpr: 'State',
    searchEnabled: true,
    onValueChanged: onStateSelected,
  };

  const eventDescriptionOptions = {
    height: "97px",
  };

  const ctaButtonOptions = {
    text: 'Next',
    type: 'default',
    onClick: onCTA,
    width: '120px',
  };

  const cancelButtonOptions = {
    text: 'Cancel',
    type: 'default',
    stylingMode: 'outlined',
    onClick: () => {props.onCancel()},
    width: '120px',
  };

  return (
    <div>
      <Form
        colCount={2}
        ref={formRef}
        formData={sifEvent}
        onFieldDataChanged={onFieldDataChanged}
        validationGroup="customerData"
      >
        <GroupItem>
          <GroupItem captionRender={renderCalendarCaption} caption="Event Date and Time">
            <Item dataField="DATE OF EVENT (m/d/yyyy)" editorType="dxDateBox" editorOptions={eventDateEditorOptions}>
              <Label text="Date"/>
              <RequiredRule message="Date is required" />
              <RangeRule
                max={maxDate}
                message="Event date can not be in the future"
              />
            </Item>
            <Item dataField="Time of Event" editorType="dxSelectBox" editorOptions={eventTimeEditorOptions}>
              <Label text="Time"/>
            </Item>
          </GroupItem>
          <GroupItem captionRender={renderLocationCaption} caption="Event Location">
            <Item dataField="Area/Basin" editorType="dxSelectBox" editorOptions={eventBasinOptions}>
              <Label text="Area/Basin"/>
            </Item>
            <Item dataField="State" editorType="dxSelectBox" editorOptions={eventStateOptions}>
              <Label text="State"/>
            </Item>
            <Item dataField="County" editorType="dxSelectBox" editorOptions={eventCountyOptions}>
              <Label text="County"/>
            </Item>
          </GroupItem>
        </GroupItem>
        <GroupItem>
          <GroupItem captionRender={renderDescriptionCaption} caption="Event Description">
            <Item dataField="Company" editorOptions={{ readOnly: true }}>
              <Label text="Company"/>
            </Item>
            <Item dataField="DESCRIPTION OF INCIDENT" editorType="dxTextArea" editorOptions={eventDescriptionOptions}>
              <Label text="Description"/>
              <StringLengthRule
                min={20}
                message="Description has to be at least 10 characters"
              />
              <RequiredRule message="Description is required" />
            </Item>
            <Item dataField="Type" editorType="dxSelectBox" editorOptions={eventTypeOptions}>
              <Label text="Event Type"/>
              <RequiredRule message="Event type is required" />
            </Item>
            <Item dataField="Number of Fatalities" visible={sifEvent["Type"]==="Actual"} editorType="dxNumberBox" editorOptions={eventNumberEditorOptions}>
              <Label text="Number of Fatalities"/>
              <RequiredRule message="Number of Fatalities is required" />
              <RangeRule
                min={0}
                max={100}
                message="Number has to be between 0 and 100"
              />
            </Item>
            <Item dataField="Number of Serious Injuries" visible={sifEvent["Type"]==="Actual"} editorType="dxNumberBox" editorOptions={eventNumberEditorOptions}>
              <Label text="Number of Serious Injuries"/>
              <RequiredRule message="Number of Serious Injuries is required" />
              <RangeRule
                min={0}
                max={100}
                message="Number has to be between 0 and 100"
              />
            </Item>
          </GroupItem>
          <GroupItem colCount={1} cssClass="buttons-group-2">
            <EmptyItem></EmptyItem>
            <GroupItem
              cssClass="d-flex flex-row justify-content-end"
              colCount={2}
            >
              <ButtonItem
                buttonOptions={cancelButtonOptions}
                name="Cancel"
              />
              <ButtonItem buttonOptions={ctaButtonOptions} />
            </GroupItem>
          </GroupItem>
        </GroupItem>
      </Form>

    </div>
  );
}
